/**
 * Quais grupos de responsável o kanban mostra.
 *
 * ## O problema que isto resolve
 *
 * O agregado do servidor traz TODOS os responsáveis com chamado. Quando o
 * filtro salvo restringe por grupo de atendimento, mostrar todos enchia a tela
 * de linhas vazias -- gente de outro grupo, sem chamado nenhum naquele filtro.
 * Daí o recorte.
 *
 * ## Por que "Não atribuído" é exceção
 *
 * O chamado não tem grupo de atendimento próprio: o grupo vem do RESPONSÁVEL.
 * Sem responsável não há grupo, então o balde "Não atribuído" nunca casa com
 * nenhuma restrição -- e era descartado junto.
 *
 * O efeito disso em produção (ponto 1 da Gisele, 15/10/2026): ao inativar um
 * operador, a API devolve os chamados dele para `assigned_to = null`. Quem
 * filtra por grupo -- o caso comum -- deixava de ver esses chamados em
 * qualquer lugar da tela. Não é "a página não atualizou": eles não tinham onde
 * aparecer.
 *
 * A decisão é dele, com o custo declarado: a coluna pode trazer órfãos de
 * outros grupos. Chamado invisível é pior que chamado a mais.
 */

const CHAVE_SEM_RESPONSAVEL = 'unassigned';

/**
 * Este responsável aparece no kanban?
 *
 * A regra por CHAVE, separada da lista, porque o kanban monta os grupos por
 * dois caminhos: o agregado do servidor e -- quando há busca, que desliga o
 * agregado -- o agrupamento dos chamados já carregados.
 *
 * Só o primeiro passava pelo recorte, e foi isso que a Gisele relatou em
 * 18/10/2026: "busca por dentro do kanban aparece nome de outros operadores
 * que não pertencem a este kanban". Mesma regra, dois caminhos.
 *
 * @param {string|number} chave  id do responsável, ou 'unassigned'
 * @param {number[]} gruposDaCondicao  grupos que o filtro salvo restringe
 * @param {object} grupoPorUsuario  id do usuário (texto) -> service_group_id
 */
export const responsavelVisivelNoKanban = (chave, gruposDaCondicao, grupoPorUsuario) => {
    const permitidos = new Set((gruposDaCondicao || []).map(Number));

    // Sem restrição, ou sem os usuários carregados -- de onde sai o grupo de
    // cada um --, não há como recortar: mostra todo mundo.
    if (permitidos.size === 0 || !grupoPorUsuario || Object.keys(grupoPorUsuario).length === 0) {
        return true;
    }

    /*
     * Sem responsável E sem departamento: sempre visível, pelo motivo
     * explicado no alto do arquivo -- não há grupo para comparar, e chamado
     * invisível é pior que chamado a mais.
     */
    if (String(chave) === CHAVE_SEM_RESPONSAVEL) return true;

    /*
     * Sem responsável MAS com departamento, o grupo está na própria chave
     * (`unassigned:<id>`), então dá para comparar de verdade.
     *
     * Era aqui que o chamado transferido por departamento sumia (ponto 25 da
     * Gisele, 20/10/2026: "ao transferir por departamento, o ticket não
     * aparece no Kanban do outro departamento"). A chave não é
     * `unassigned` exato, então escapava da isenção acima; e
     * `grupoPorUsuario["unassigned:12"]` é `undefined`, então caía no
     * `return false` logo abaixo. O grupo era descartado inteiro, sem erro
     * nenhum, em TODO kanban que restringe por grupo de atendimento -- que é
     * o caso do "Kanban - Comercial".
     *
     * Aqui não vale a isenção do caso anterior: o departamento é conhecido,
     * e mostrar o chamado que espera o Suporte dentro do quadro do Comercial
     * seria o defeito oposto.
     */
    const doDepartamento = String(chave || '').match(/^unassigned:(\d+)$/);

    if (doDepartamento) {
        return permitidos.has(Number(doDepartamento[1]));
    }

    const grupoDoResponsavel = grupoPorUsuario[String(chave)];

    return grupoDoResponsavel != null && permitidos.has(Number(grupoDoResponsavel));
};

/**
 * @param {Array|null} agregado  os grupos do servidor (`assigneeCounts`)
 * @param {number[]} gruposDaCondicao  grupos que o filtro salvo restringe
 * @param {object} grupoPorUsuario  id do usuário (texto) -> service_group_id
 */
export const responsaveisVisiveisNoKanban = (agregado, gruposDaCondicao, grupoPorUsuario) => {
    // `null` é "o agregado ainda não chegou", e o kanban trata isso. Virar `[]`
    // aqui faria a tela dizer "nenhum responsável" durante o carregamento.
    if (!Array.isArray(agregado)) return agregado;

    // Sem restrição nenhuma, devolve a MESMA lista: o kanban recebe isto como
    // prop, e criar array novo a cada passagem só gera trabalho de render sem
    // nada em troca.
    const permitidos = new Set((gruposDaCondicao || []).map(Number));
    if (permitidos.size === 0 || !grupoPorUsuario || Object.keys(grupoPorUsuario).length === 0) {
        return agregado;
    }

    // A MESMA regra do outro caminho, aplicada item a item. Se as duas fossem
    // escritas separadas, elas divergiriam -- e divergir aqui significa a
    // busca mostrando gente que a lista não mostra.
    return agregado.filter((grupo) => responsavelVisivelNoKanban(grupo?.key, gruposDaCondicao, grupoPorUsuario));
};
